/**
 * Admin → Contract detail + edit.
 *
 * SPEC §9.3: contract terms are immutable post-activation. So this
 * screen renders SLA / BAU term fields read-only, and the editable
 * surface covers only renewal-y fields: name, end_date, account
 * owner, slack channel, harvest project, status (cancel).
 *
 * "Renew" is a quick action that flips into edit mode focused on
 * end_date.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ArrowUpRight, Clock, Copy, Hash, Link as LinkIcon, Pencil, ScrollText, User } from 'lucide-react';
import { toast } from 'sonner';

import type { ContractAuditEntry, ContractUsage } from '@beacon/api-client';

import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import {
  useContract,
  useContractAudit,
  useContractMetrics,
  useContractUsage,
  useUpdateContract,
} from '@/hooks/use-contracts.js';
import { useOrganization } from '@/hooks/use-organizations.js';
import { useAssignableUsers } from '@/hooks/use-users.js';
import { cn } from '@/lib/cn.js';
import { beaconClient } from '@/lib/api/client.js';

import { Avatar } from '@/components/beacon/avatar.js';
import { ContractLogo } from '@/components/beacon/contract-logo.js';
import { contractDisplay } from '@/lib/contract-display.js';
import { NewContractDialog } from './NewContractDialog.js';

type TabId = 'overview' | 'tickets' | 'reports' | 'users' | 'audit';

export function ContractDetail() {
  const { orgId, contractId } = useParams();
  const orgQuery = useOrganization(orgId);
  const contractQuery = useContract(contractId);
  const usersQuery = useAssignableUsers();
  const update = useUpdateContract();

  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [editing, setEditing] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [name, setName] = useState('');
  const [endDate, setEndDate] = useState('');
  const [accountOwnerUserId, setAccountOwnerUserId] = useState('');
  const [slackChannel, setSlackChannel] = useState('');
  const [harvestProjectId, setHarvestProjectId] = useState('');
  const [status, setStatus] = useState<'active' | 'expired' | 'cancelled'>(
    'active'
  );

  useEffect(() => {
    if (!contractQuery.data) return;
    const c = contractQuery.data;
    setName(c.name);
    setEndDate(c.endDate);
    setAccountOwnerUserId(c.accountOwnerUserId);
    setSlackChannel(c.slackChannel ?? '');
    setHarvestProjectId(c.harvestProjectId ?? '');
    setStatus(c.status);
  }, [contractQuery.data]);

  if (contractQuery.isLoading) {
    return <ContractDetailSkeleton />;
  }
  if (contractQuery.isError || !contractQuery.data) {
    return (
      <div className="p-8 text-ink-300">
        Contract not found.{' '}
        <Link to="/admin/clients" className="text-beacon-300 hover:underline">
          Back to clients
        </Link>
      </div>
    );
  }
  const contract = contractQuery.data;
  const org = orgQuery.data;
  const users = usersQuery.data ?? [];
  const ownerName = users.find((u) => u.id === contract.accountOwnerUserId)?.name;

  function saveEdits() {
    update.mutate(
      {
        id: contract.id,
        input: {
          name: name.trim() || contract.name,
          endDate,
          accountOwnerUserId,
          slackChannel: slackChannel.trim() || null,
          harvestProjectId: harvestProjectId.trim() || null,
          status,
        },
      },
      {
        onSuccess: () => {
          toast.success('Contract updated');
          setEditing(false);
        },
      }
    );
  }

  function cancelEdits() {
    setName(contract.name);
    setEndDate(contract.endDate);
    setAccountOwnerUserId(contract.accountOwnerUserId);
    setSlackChannel(contract.slackChannel ?? '');
    setHarvestProjectId(contract.harvestProjectId ?? '');
    setStatus(contract.status);
    setEditing(false);
  }

  return (
    <div className="h-full overflow-y-auto bg-ink-900">
      <header className="px-6 pt-4 pb-0 border-b border-ink-600 bg-ink-850 sticky top-0 z-10">
        <div className="flex items-center gap-2 text-[12px] text-ink-300 mb-2">
          <Link
            to="/admin/clients"
            className="hover:text-ink-100 inline-flex items-center gap-1"
          >
            Contracts
          </Link>
          <span>›</span>
          <span className="text-ink-200">{contract.name}</span>
        </div>
        <div className="flex items-start gap-3 pb-4">
          <ContractLogo contract={contractDisplay(contract)} size={44} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-[20px] font-semibold text-ink-100">
                {contract.name}
              </h1>
              <Badge
                tone="amber"
                dot
              >
                {contract.type.toUpperCase()}
                {(contract.type === 'sla' || contract.type === 'warranty') && 'monthlyHours' in contract
                  ? ` · ${contract.monthlyHours}h / month`
                  : contract.type === 'bau' && 'totalHours' in contract
                    ? ` · ${contract.totalHours}h total`
                    : ''}
              </Badge>
              <Badge
                tone={
                  contract.status === 'active'
                    ? 'green'
                    : contract.status === 'expired'
                      ? 'yellow'
                      : 'neutral'
                }
                dot
              >
                {contract.status.charAt(0).toUpperCase() + contract.status.slice(1)}
              </Badge>
              <span className="text-[12px] mono text-ink-300">
                · {formatDate(contract.startDate)} → {formatDate(contract.endDate)}
              </span>
            </div>
            <div className="flex items-center gap-4 mt-1.5 text-[12.5px] text-ink-300">
              {ownerName && (
                <span className="inline-flex items-center gap-1.5">
                  <User size={13} />
                  Owner <span className="text-ink-100">{ownerName}</span>
                </span>
              )}
              {contract.slackChannel && (
                <span className="inline-flex items-center gap-1.5">
                  <Hash size={13} />
                  <span className="mono">{contract.slackChannel.replace(/^#/, '')}</span>
                </span>
              )}
              {contract.harvestProjectId && (
                <a
                  href={`https://conducthq.harvestapp.com/time/?project=${encodeURIComponent(contract.harvestProjectId)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-beacon-300 hover:text-beacon-200 hover:underline"
                >
                  <LinkIcon size={13} />
                  <span className="mono">
                    harvest/{contract.harvestProjectId}
                    <ArrowUpRight size={10} className="inline -mt-0.5 ml-0.5" />
                  </span>
                </a>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {editing ? (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={cancelEdits}
                  disabled={update.isPending}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={saveEdits}
                  disabled={update.isPending}
                >
                  {update.isPending ? 'Saving…' : 'Save'}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditing(true)}
                >
                  <Pencil size={13} />
                  Edit
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setDuplicating(true)}
                  title="Create a new contract pre-filled from this one"
                >
                  <Copy size={11} />
                  Duplicate
                </Button>
              </>
            )}
          </div>
        </div>
        <nav role="tablist" aria-label="Contract sections" className="flex gap-1">
          <TabButton
            active={activeTab === 'overview'}
            onClick={() => setActiveTab('overview')}
            id="ct-tab-overview"
          >
            Overview
          </TabButton>
          <TabButton
            active={activeTab === 'tickets'}
            onClick={() => setActiveTab('tickets')}
            id="ct-tab-tickets"
          >
            Tickets
          </TabButton>
          <TabButton
            active={activeTab === 'reports'}
            onClick={() => setActiveTab('reports')}
            id="ct-tab-reports"
          >
            Reports
          </TabButton>
          <TabButton
            active={activeTab === 'users'}
            onClick={() => setActiveTab('users')}
            id="ct-tab-users"
          >
            Users
          </TabButton>
          <TabButton
            active={activeTab === 'audit'}
            onClick={() => setActiveTab('audit')}
            id="ct-tab-audit"
            icon={<ScrollText size={12} />}
          >
            Audit
          </TabButton>
        </nav>
      </header>

      {activeTab === 'tickets' && (
        <TicketsTab contractId={contract.id} />
      )}

      {activeTab === 'reports' && (
        <ReportsTab contractId={contract.id} />
      )}

      {activeTab === 'users' && (
        <UsersTab orgId={orgId ?? ''} />
      )}

      {activeTab === 'audit' && <AuditTab contractId={contract.id} />}

      {activeTab === 'overview' && (
        <OverviewTab
          contract={contract}
          users={users}
          editing={editing}
          name={name}
          setName={setName}
          endDate={endDate}
          setEndDate={setEndDate}
          accountOwnerUserId={accountOwnerUserId}
          setAccountOwnerUserId={setAccountOwnerUserId}
          slackChannel={slackChannel}
          setSlackChannel={setSlackChannel}
          harvestProjectId={harvestProjectId}
          setHarvestProjectId={setHarvestProjectId}
          status={status}
          setStatus={setStatus}
        />
      )}

      {/* OrganizationDetail uses plain string ids; the dialog accepts
       *  the broader Organization shape with branded Uuid. The
       *  structural shape is otherwise identical, so cast at the
       *  boundary rather than complicate the dialog's prop type. */}
      <NewContractDialog
        organization={
          duplicating && org
            ? (org as unknown as import('@beacon/core').Organization)
            : null
        }
        duplicateFrom={duplicating ? contract : null}
        onClose={() => setDuplicating(false)}
      />
    </div>
  );
}

function ContractDetailSkeleton() {
  return (
    <div className="h-full overflow-y-auto bg-ink-900" aria-busy="true">
      <header className="px-6 pt-4 pb-3 border-b border-ink-600 bg-ink-850">
        <Skeleton className="h-3 w-48 mb-2" />
        <Skeleton className="h-5 w-2/3 max-w-[420px] mb-2" />
        <div className="flex items-center gap-2">
          <Skeleton className="h-5 w-12" inline />
          <Skeleton className="h-5 w-16" inline />
          <Skeleton className="h-3 w-44" inline />
        </div>
      </header>
      <div className="p-6 max-w-3xl space-y-5">
        {(['Editable', 'Terms'] as const).map((label) => (
          <section key={label}>
            <Skeleton className="h-2.5 w-24 mb-2" />
            <div className="rounded-md border border-ink-600 bg-ink-850 p-4 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  className="grid grid-cols-[10rem,1fr] items-center gap-3"
                >
                  <Skeleton className="h-3 w-24" />
                  <Skeleton className="h-3 w-3/4 max-w-[260px]" />
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------
 * Overview tab — design handoff: screen-contract.jsx
 *   12-col grid: usage(8) + SLA health(4), chart(8) + terms(4),
 *   recent tickets(12). Usage bar is a timeline with projected
 *   overlay and date axis. Chart has 140px height with dashed
 *   allocation line.
 * ---------------------------------------------------------------- */
interface OverviewTabProps {
  contract: import('@beacon/core').Contract;
  users: Array<{ id: string; name: string; email: string }>;
  editing: boolean;
  name: string;
  setName: (v: string) => void;
  endDate: string;
  setEndDate: (v: string) => void;
  accountOwnerUserId: string;
  setAccountOwnerUserId: (v: string) => void;
  slackChannel: string;
  setSlackChannel: (v: string) => void;
  harvestProjectId: string;
  setHarvestProjectId: (v: string) => void;
  status: 'active' | 'expired' | 'cancelled';
  setStatus: (v: 'active' | 'expired' | 'cancelled') => void;
}

function OverviewTab({ contract, users, editing, name, setName, endDate, setEndDate, accountOwnerUserId, setAccountOwnerUserId, slackChannel, setSlackChannel, harvestProjectId, setHarvestProjectId, status, setStatus }: OverviewTabProps) {
  const metricsQuery = useContractMetrics(contract.id);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const metrics: any = metricsQuery.data;
  const usage = metrics?.usage as { usedHours: number; allocatedHours: number; remainingHours: number; daysLeft?: number; projectedEom?: number; periodStart?: string; periodEnd?: string } | undefined;

  const isSlaLike = contract.type === 'sla' || contract.type === 'warranty';
  const isBau = contract.type === 'bau';
  const allocation = usage?.allocatedHours ?? (isSlaLike && 'monthlyHours' in contract ? contract.monthlyHours : isBau && 'totalHours' in contract ? contract.totalHours : 0);

  const loading = metricsQuery.isLoading;

  // Usage bar percentages for timeline
  const usedPct = allocation > 0 ? Math.min(100, (usage?.usedHours ?? 0) / allocation * 100) : 0;
  const projectedPct = allocation > 0 && usage?.projectedEom != null ? Math.min(100, usage.projectedEom / allocation * 100) : usedPct;

  // Chart scale: ensure bars are visible even with low data
  const chartHeight = 140;
  const monthlyData: Array<{ month: string; hours: number }> = metrics?.monthlyUsage ?? [];
  const chartMax = Math.max(allocation, ...monthlyData.map((x) => x.hours), 1) * 1.2;

  return (
    <div className="overflow-auto scrollbar-thin px-6 py-6 bg-ink-900">
      <div className="grid grid-cols-12 gap-5">

        {/* ── Hero usage card — col-span-8 ── */}
        {loading && (
          <div className="col-span-12 lg:col-span-8 rounded-md border border-ink-600 bg-ink-850 p-5 space-y-3">
            <div className="flex justify-between"><Skeleton className="h-3 w-28" /><div className="flex gap-5"><Skeleton className="h-8 w-16" /><Skeleton className="h-8 w-12" /><Skeleton className="h-8 w-16" /></div></div>
            <Skeleton className="h-10 w-40" />
            <Skeleton className="h-3 w-full rounded-full" />
            <div className="flex gap-4"><Skeleton className="h-3 w-24" /><Skeleton className="h-3 w-28" /><Skeleton className="h-3 w-24" /></div>
          </div>
        )}
        {!loading && usage && (
          <div className="col-span-12 lg:col-span-8 rounded-md border border-ink-600 bg-ink-850 p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10.5px] uppercase tracking-wider text-ink-300 font-semibold">
                  {isBau ? 'Lifetime usage' : `${new Date().toLocaleDateString('en-AU', { month: 'long', year: 'numeric', timeZone: 'Australia/Sydney' })} usage`}
                </div>
                <div className="mt-1 flex items-baseline gap-2">
                  <span className="mono text-[36px] font-semibold text-ink-100 leading-none">
                    {usage.usedHours.toFixed(2)}
                  </span>
                  <span className="text-[14px] text-ink-300">
                    of {allocation.toFixed(2)} hours
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-6 text-right">
                <div>
                  <div className="text-[10.5px] uppercase tracking-wider text-ink-300">Remaining</div>
                  <div className="mono text-[18px] font-semibold text-ok-400">{usage.remainingHours.toFixed(2)} h</div>
                </div>
                {usage.daysLeft != null && (
                  <div>
                    <div className="text-[10.5px] uppercase tracking-wider text-ink-300">Days left</div>
                    <div className="mono text-[18px] font-semibold">{usage.daysLeft}</div>
                  </div>
                )}
                {usage.projectedEom != null && (
                  <div>
                    <div className="text-[10.5px] uppercase tracking-wider text-ink-300">Projected EoM</div>
                    <div className={cn('mono text-[18px] font-semibold', usage.projectedEom > allocation ? 'text-danger-400' : usage.projectedEom > allocation * 0.8 ? 'text-warn-400' : 'text-ink-100')}>
                      {usage.projectedEom.toFixed(2)} h
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Timeline progress bar */}
            <div className="mt-2">
              <div className="relative h-3 w-full bg-ink-700 rounded-full overflow-hidden">
                <div className="absolute inset-y-0 left-0 bg-beacon-400/30 rounded-full" style={{ width: `${projectedPct}%` }} />
                <div className="absolute inset-y-0 left-0 bg-beacon-400 rounded-full" style={{ width: `${usedPct}%` }} />
                {[25, 50, 75].map((p) => (
                  <div key={p} className="absolute inset-y-0 w-px bg-ink-900/60" style={{ left: `${p}%` }} />
                ))}
              </div>
              <div className="flex justify-between text-[11px] text-ink-300 mono mt-1.5">
                <span>{usage.periodStart ? shortDay(usage.periodStart) : '01'}</span>
                <span className="text-beacon-300">Today · {shortDay(new Date().toISOString().slice(0, 10))}</span>
                <span>{usage.periodEnd ? shortDay(usage.periodEnd) : '30'}</span>
              </div>
              <div className="mt-3 flex items-center gap-4 text-[11.5px]">
                <LegendDot color="bg-beacon-400" label={`Used · ${usage.usedHours.toFixed(2)} h`} />
                {usage.projectedEom != null && (
                  <LegendDot color="bg-beacon-400/30" label={`Projected · +${(usage.projectedEom - usage.usedHours).toFixed(2)} h`} />
                )}
                <LegendDot color="bg-ink-700" label={`Allocation · ${allocation.toFixed(2)} h`} />
              </div>
            </div>
          </div>
        )}

        {/* ── SLA Health card — col-span-4 (SLA/Warranty only) ── */}
        {loading && isSlaLike && (
          <div className="col-span-12 lg:col-span-4 rounded-md border border-ink-600 bg-ink-850 p-5 space-y-3">
            <Skeleton className="h-3 w-32" />
            <div className="grid grid-cols-2 gap-3">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="bg-ink-800 rounded-md p-3 space-y-2"><Skeleton className="h-2.5 w-20" /><Skeleton className="h-6 w-16" /><Skeleton className="h-2.5 w-12" /></div>)}</div>
          </div>
        )}
        {!loading && isSlaLike && metrics?.slaHealth && (
          <div className="col-span-12 lg:col-span-4 rounded-md border border-ink-600 bg-ink-850 p-5">
            <div className="text-[10.5px] uppercase tracking-wider text-ink-300 font-semibold mb-3">
              SLA health · {metrics.slaHealth.quarterLabel}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <StatCard label="Avg response" value={formatMinutes(metrics.slaHealth.avgResponseMin)} sub={isSlaLike && 'responseTargetMinutes' in contract ? `Target ${formatMinutes(contract.responseTargetMinutes)}` : undefined} tone="ok" />
              <StatCard label="Avg solution" value={formatMinutes(metrics.slaHealth.avgSolutionMin)} sub={isSlaLike && 'solutionTargetMinutes' in contract ? `Target ${formatMinutes(contract.solutionTargetMinutes)}` : undefined} tone={metrics.slaHealth.avgSolutionMin > (('solutionTargetMinutes' in contract ? contract.solutionTargetMinutes : 0) || Infinity) ? 'warn' : 'ok'} />
              <StatCard label="Response met" value={`${metrics.slaHealth.responseMetPct}%`} sub={`${metrics.slaHealth.responseMetCount} / ${metrics.slaHealth.responseTotalCount}`} tone={metrics.slaHealth.responseMetPct < 95 ? 'warn' : 'ok'} />
              <StatCard label="Breaches" value={String(metrics.slaHealth.breaches)} sub={`${metrics.slaHealth.quarterLabel} to date`} tone={metrics.slaHealth.breaches > 0 ? 'warn' : 'ok'} />
            </div>
          </div>
        )}

        {/* ── 12-month chart — col-span-8 ── */}
        {loading && (
          <div className="col-span-12 lg:col-span-8 rounded-md border border-ink-600 bg-ink-850 p-5 space-y-4">
            <div className="flex justify-between"><Skeleton className="h-3 w-44" /><Skeleton className="h-3 w-32" /></div>
            <div className="flex items-end gap-2 h-[140px]">{[40, 60, 55, 30, 45, 35, 50, 25, 65, 70, 45, 55].map((h, i) => <div key={i} className="flex-1 flex items-end justify-center"><div className="w-full rounded-t-[3px] bg-ink-700 animate-pulse" style={{ height: `${h}%` }} /></div>)}</div>
          </div>
        )}
        {!loading && monthlyData.length > 0 && (
          <div className="col-span-12 lg:col-span-8 rounded-md border border-ink-600 bg-ink-850 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10.5px] uppercase tracking-wider text-ink-300 font-semibold">Monthly usage · trailing 12 months</div>
              <div className="flex items-center gap-3 text-[11.5px] mono text-ink-300">
                <LegendDot color="bg-beacon-400" label="Hours" />
                {allocation > 0 && <LegendDot color="bg-ink-500 outline outline-1" label={`Allocation (${allocation}h)`} />}
              </div>
            </div>
            <div className="pt-4">
              <div className="relative flex items-end gap-2 border-b border-ink-600" style={{ height: chartHeight }}>
                {/* Allocation dashed line */}
                {allocation > 0 && (
                  <div
                    className="absolute left-0 right-0 border-t border-dashed border-ink-400/60"
                    style={{ bottom: `${(allocation / chartMax) * chartHeight}px` }}
                  >
                    <span className="absolute right-0 -top-4 text-[10px] mono text-ink-300 bg-ink-850 px-1">
                      {allocation}h alloc
                    </span>
                  </div>
                )}
                {monthlyData.map((m, i) => {
                  const barH = Math.max(2, (m.hours / chartMax) * chartHeight);
                  const isCurrentMonth = i === monthlyData.length - 1;
                  return (
                    <div key={m.month} className="flex-1 flex items-end justify-center group">
                      <div
                        className={cn(
                          'w-full rounded-t-[3px] transition-all duration-400',
                          isCurrentMonth ? 'bg-beacon-400' : 'bg-ink-500 group-hover:bg-ink-400'
                        )}
                        style={{ height: `${barH}px` }}
                        title={`${m.month}: ${m.hours}h`}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-2 mt-1.5">
                {monthlyData.map((m, i) => (
                  <div key={m.month} className={cn('flex-1 text-center text-[10.5px] mono', i === monthlyData.length - 1 ? 'text-beacon-300 font-semibold' : 'text-ink-300')}>
                    {m.month}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Contract summary — col-span-4 ── */}
        <div className="col-span-12 lg:col-span-4 rounded-md border border-ink-600 bg-ink-850 p-5">
          <div className="text-[10.5px] uppercase tracking-wider text-ink-300 font-semibold mb-3">Contract</div>
          <dl className="text-[12.5px] space-y-2">
            <TermRow k="SLA" v={`${contract.type.toUpperCase()}${isSlaLike ? ' — Priority Response' : ''}`} />
            {isSlaLike && 'responseTargetMinutes' in contract && (
              <>
                <TermRow k="Response target" v={formatMinutes(contract.responseTargetMinutes)} bold />
                <TermRow k="Solution target" v={formatMinutes(contract.solutionTargetMinutes)} bold />
                <TermRow k="Hours / month" v={contract.monthlyHours.toFixed(2)} bold />
                <TermRow k="Carry-over" v="None" />
                {contract.type === 'sla' && contract.overageRateCents > 0 && (
                  <TermRow k="Billing" v={`A$ ${((contract.monthlyHours * contract.overageRateCents) / 100).toLocaleString('en-AU')} / month (inc GST)`} />
                )}
                {contract.type === 'warranty' && <TermRow k="Billing" v="Included (warranty)" />}
              </>
            )}
            {isBau && 'totalHours' in contract && (
              <>
                <TermRow k="Total hours" v={contract.totalHours.toFixed(2)} bold />
                <TermRow k="Duration" v={`${contract.durationMonths} months`} bold />
              </>
            )}
            <TermRow k="Start" v={formatDate(contract.startDate)} bold />
            <TermRow k="Renews" v={formatDate(contract.endDate)} bold />
            <TermRow k="Timezone" v="Australia/Sydney" />
          </dl>
        </div>

        {/* ── Recent tickets — col-span-12 ── */}
        {loading && (
          <div className="col-span-12 rounded-md border border-ink-600 bg-ink-850 p-5 space-y-3">
            <Skeleton className="h-3 w-28" />
            {Array.from({ length: 3 }).map((_, i) => <div key={i} className="flex gap-4"><Skeleton className="h-4 w-14" /><Skeleton className="h-4 flex-1" /><Skeleton className="h-4 w-20" /><Skeleton className="h-4 w-10" /><Skeleton className="h-4 w-20" /><Skeleton className="h-4 w-12" /><Skeleton className="h-4 w-16" /></div>)}
          </div>
        )}
        {!loading && metrics?.recentTickets && metrics.recentTickets.length > 0 && (
          <div className="col-span-12 rounded-md border border-ink-600 bg-ink-850">
            <div className="px-4 py-3 flex items-center border-b border-ink-600">
              <div className="text-[10.5px] uppercase tracking-wider text-ink-300 font-semibold">Recent tickets</div>
              <Link to="/inbox" className="ml-auto text-[12px] text-beacon-300 hover:underline inline-flex items-center gap-0.5">
                View all {metrics.recentTickets.length} <ArrowUpRight size={11} />
              </Link>
            </div>
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="text-left text-[10.5px] uppercase tracking-wider text-ink-300 border-b border-ink-600">
                  <th className="py-2 px-4 w-[70px]">Ticket</th>
                  <th className="py-2 pr-3">Subject</th>
                  <th className="py-2 pr-3 w-[110px]">Status</th>
                  <th className="py-2 pr-3 w-[60px]">Pri</th>
                  <th className="py-2 pr-3 w-[110px]">Assignee</th>
                  <th className="py-2 pr-3 w-[70px] text-right">Hours</th>
                  <th className="py-2 pr-4 w-[90px] text-right">Updated</th>
                </tr>
              </thead>
              <tbody>
                {metrics.recentTickets.map((t: { number: string; subject: string; status: string; priority: string; assigneeUserId: string | null; hoursLogged: number; updatedAt: string }) => {
                  const assignee = users.find((u) => u.id === t.assigneeUserId);
                  return (
                    <tr key={t.number} className="border-b border-ink-600/60 hover:bg-ink-850 transition-colors duration-75">
                      <td className="py-2.5 px-4 mono text-ink-300">
                        <Link to={`/tickets/${t.number}`} className="text-beacon-300 hover:underline">{t.number}</Link>
                      </td>
                      <td className="py-2.5 pr-3 text-ink-100 truncate max-w-0">
                        <span className="truncate inline-block max-w-full align-middle">{t.subject}</span>
                      </td>
                      <td className="py-2.5 pr-3">
                        <Badge tone={statusTone(t.status)}>{t.status.replace('_', ' ')}</Badge>
                      </td>
                      <td className="py-2.5 pr-3">
                        <Badge tone={t.priority === 'p1' ? 'red' : t.priority === 'p2' ? 'amber' : 'slate'}>{t.priority.toUpperCase()}</Badge>
                      </td>
                      <td className="py-2.5 pr-3">
                        {assignee ? (
                          <div className="flex items-center gap-1.5">
                            <Avatar name={assignee.name} size={18} />
                            <span>{assignee.name.split(' ')[0]}</span>
                          </div>
                        ) : (
                          <span className="text-ink-400 italic">—</span>
                        )}
                      </td>
                      <td className="py-2.5 pr-3 text-right mono">{t.hoursLogged.toFixed(2)}</td>
                      <td className="py-2.5 pr-4 text-right mono text-ink-300">{shortTime(t.updatedAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Editable fields (only visible in edit mode) ── */}
      {editing && (
        <div className="max-w-3xl space-y-5 mt-6">
          <Section title="Editable">
            <Field label="Name">
              <Input type="text" value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field label="End date (renewal)">
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </Field>
            <Field label="Account owner">
              <select value={accountOwnerUserId} onChange={(e) => setAccountOwnerUserId(e.target.value)} className="h-8 px-2.5 text-[13px] bg-ink-800 border border-ink-600 rounded-md w-full focus-ring">
                {users.map((u) => (<option key={u.id} value={u.id}>{u.name} · {u.email}</option>))}
              </select>
            </Field>
            <Field label="Slack webhook URL">
              <Input type="url" value={slackChannel} placeholder="https://hooks.slack.com/services/..." onChange={(e) => setSlackChannel(e.target.value)} />
            </Field>
            <Field label="Harvest project ID">
              <Input type="text" value={harvestProjectId} onChange={(e) => setHarvestProjectId(e.target.value)} />
            </Field>
            <Field label="Status">
              <div className="flex gap-1.5">
                {(['active', 'expired', 'cancelled'] as const).map((s) => (
                  <button key={s} onClick={() => setStatus(s)} className={'h-7 px-3 rounded-md text-[12.5px] focus-ring border ' + (status === s ? 'bg-ink-700 border-ink-500 text-ink-100' : 'bg-ink-800 border-ink-600 text-ink-300 hover:text-ink-100')}>{s}</button>
                ))}
              </div>
            </Field>
          </Section>
        </div>
      )}
    </div>
  );
}

/* Design handoff primitives — screen-contract.jsx */

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-ink-300">
      <span className={cn('w-2 h-2 rounded-full', color)} />
      {label}
    </span>
  );
}

function StatCard({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: 'ok' | 'warn' | 'neutral' }) {
  const toneC = tone === 'ok' ? 'text-ok-400' : tone === 'warn' ? 'text-warn-400' : 'text-ink-100';
  return (
    <div className="bg-ink-850 border border-ink-600 rounded-md p-2.5">
      <div className="text-[10.5px] uppercase tracking-wider text-ink-300">{label}</div>
      <div className={cn('mono text-[18px] font-semibold mt-0.5', toneC)}>{value}</div>
      {sub && <div className="text-[10.5px] text-ink-400 mono">{sub}</div>}
    </div>
  );
}

function TermRow({ k, v, bold }: { k: string; v: string; bold?: boolean }) {
  return (
    <div className="flex">
      <dt className="w-[120px] text-ink-300">{k}</dt>
      <dd className={cn('flex-1 text-ink-100', bold && 'font-medium')}>{v}</dd>
    </div>
  );
}

function formatMinutes(min: number): string {
  if (min < 60) return `${Math.round(min)}m`;
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function formatDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Australia/Sydney' });
}

function shortDay(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', timeZone: 'Australia/Sydney' });
}

function shortTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', timeZone: 'Australia/Sydney' });
}

function statusTone(s: string): 'green' | 'blue' | 'violet' | 'amber' | 'slate' | 'red' {
  if (s === 'new') return 'blue';
  if (s === 'triaged') return 'slate';
  if (s === 'in_progress') return 'violet';
  if (s === 'awaiting_client') return 'amber';
  if (s === 'resolved') return 'green';
  return 'slate';
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h2 className="text-[11px] uppercase tracking-[0.06em] text-ink-300 mb-2">
        {title}
      </h2>
      <div className="rounded-md border border-ink-600 bg-ink-850 p-4 space-y-3">
        {children}
      </div>
    </section>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-[10rem,1fr] items-center gap-3">
      <span className="text-[12px] text-ink-300">{label}</span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  icon?: React.ReactNode;
  id: string;
}

function TabButton({ active, onClick, children, icon, id }: TabButtonProps) {
  return (
    <button
      type="button"
      role="tab"
      id={id}
      aria-selected={active}
      onClick={onClick}
      data-testid={id}
      className={cn(
        'inline-flex items-center gap-1.5 h-9 px-3 text-[12.5px] font-medium border-b-2 -mb-px focus-ring transition-colors',
        active
          ? 'border-beacon-400 text-ink-100'
          : 'border-transparent text-ink-300 hover:text-ink-100'
      )}
    >
      {icon}
      {children}
    </button>
  );
}

/**
 * Usage tab — period-scoped roll-up.
 *
 * SLA: current calendar month, hours used vs monthly_hours, % bar.
 * BAU: lifetime since contract start, hours used vs total_hours,
 *      % bar + remaining hours, plus a list of monthly slices for
 *      burn-rate visibility (SPEC §10 BAU burn-rate alert source).
 * Internal: no usage budget — show a friendly note.
 */
function UsageTab({
  contractId,
  contractType,
}: {
  contractId: string;
  contractType: 'sla' | 'warranty' | 'bau' | 'internal';
}) {
  const usageQuery = useContractUsage(contractId);

  if (contractType === 'internal') {
    return (
      <div className="p-6 max-w-3xl">
        <div className="rounded-md border border-ink-600 bg-ink-850 p-6 text-[13px] text-ink-300">
          Internal contracts have no hour budget. Time logged here is for
          accounting/visibility only.
        </div>
      </div>
    );
  }
  if (usageQuery.isLoading) {
    return <UsageSkeleton />;
  }
  if (usageQuery.isError || !usageQuery.data) {
    return (
      <div className="p-6 max-w-3xl">
        <div className="rounded-md border border-danger-500/30 bg-danger-500/5 p-4 text-[13px] text-danger-400">
          Couldn't load usage data.
        </div>
      </div>
    );
  }
  const usage = usageQuery.data;

  return (
    <div className="p-6 max-w-3xl space-y-5" data-testid="ct-usage-pane">
      {usage.type === 'sla' && <SlaUsageCard usage={usage} />}
      {usage.type === 'bau' && <BauUsageCard usage={usage} />}
    </div>
  );
}

function SlaUsageCard({
  usage,
}: {
  usage: Extract<ContractUsage, { type: 'sla' }>;
}) {
  const { usedHours, allocatedHours, remainingHours, percent } = usage;
  return (
    <Section title="SLA — current month">
      <p className="text-[11.5px] text-ink-400 -mt-1.5 mb-2">
        {formatYmd(usage.periodStartDate)} → {formatYmd(usage.periodEndDate)}
      </p>
      <div className="flex items-baseline gap-3 mb-2">
        <span className="text-[28px] font-semibold mono text-ink-100">
          {usedHours.toFixed(1)}
        </span>
        <span className="text-[14px] text-ink-300 mono">
          / {allocatedHours} h
        </span>
        <span
          className={cn(
            'ml-auto text-[12.5px] mono',
            percent >= 100
              ? 'text-danger-400'
              : percent >= 80
                ? 'text-warn-400'
                : 'text-ink-300'
          )}
        >
          {percent.toFixed(0)}%
        </span>
      </div>
      <UsageBar percent={percent} />
      <div className="mt-3 grid grid-cols-2 gap-3 text-[12px]">
        <Stat label="Hours remaining" value={`${remainingHours.toFixed(1)} h`} />
        <Stat
          label="Status"
          value={
            percent >= 100
              ? 'Over budget'
              : percent >= 80
                ? 'Approaching limit'
                : 'On track'
          }
          tone={percent >= 100 ? 'danger' : percent >= 80 ? 'warn' : 'ok'}
        />
      </div>
    </Section>
  );
}

function BauUsageCard({
  usage,
}: {
  usage: Extract<ContractUsage, { type: 'bau' }>;
}) {
  const { usedHours, budgetHours, remainingHours, percent } = usage;
  return (
    <Section title="BAU — lifetime to date">
      <p className="text-[11.5px] text-ink-400 -mt-1.5 mb-2">
        Hours drawn since contract start vs total budget. BAU bucket
        rolls implicitly month-to-month; expiry is end-of-term.
      </p>
      <div className="flex items-baseline gap-3 mb-2">
        <span className="text-[28px] font-semibold mono text-ink-100">
          {usedHours.toFixed(1)}
        </span>
        <span className="text-[14px] text-ink-300 mono">
          / {budgetHours} h
        </span>
        <span
          className={cn(
            'ml-auto text-[12.5px] mono',
            percent >= 100
              ? 'text-danger-400'
              : percent >= 80
                ? 'text-warn-400'
                : 'text-ink-300'
          )}
        >
          {percent.toFixed(0)}%
        </span>
      </div>
      <UsageBar percent={percent} />
      <div className="mt-3 grid grid-cols-2 gap-3 text-[12px]">
        <Stat label="Hours remaining" value={`${remainingHours.toFixed(1)} h`} />
        <Stat
          label="Status"
          value={
            percent >= 100
              ? 'Bucket exhausted'
              : percent >= 80
                ? 'Burn-rate watch'
                : 'On track'
          }
          tone={percent >= 100 ? 'danger' : percent >= 80 ? 'warn' : 'ok'}
        />
      </div>
    </Section>
  );
}

function UsageBar({ percent }: { percent: number }) {
  const clamped = Math.min(100, Math.max(0, percent));
  const tone =
    percent >= 100 ? 'danger' : percent >= 80 ? 'warn' : 'ok';
  return (
    <div
      className="h-2 bg-ink-800 rounded-full overflow-hidden"
      role="progressbar"
      aria-valuenow={Math.round(percent)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label="Usage"
    >
      <div
        className={cn(
          'h-full transition-[width] duration-300',
          tone === 'danger'
            ? 'bg-danger-500'
            : tone === 'warn'
              ? 'bg-warn-500'
              : 'bg-ok-500'
        )}
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'ok' | 'warn' | 'danger';
}) {
  return (
    <div className="rounded-md border border-ink-600 bg-ink-900/40 px-3 py-2">
      <div className="text-[11px] text-ink-400 uppercase tracking-[0.06em]">
        {label}
      </div>
      <div
        className={cn(
          'text-[13.5px] mono mt-0.5',
          tone === 'danger'
            ? 'text-danger-400'
            : tone === 'warn'
              ? 'text-warn-400'
              : 'text-ink-100'
        )}
      >
        {value}
      </div>
    </div>
  );
}

function UsageSkeleton() {
  return (
    <div className="p-6 max-w-3xl space-y-5" aria-busy="true">
      <div className="rounded-md border border-ink-600 bg-ink-850 p-4 space-y-3">
        <Skeleton className="h-3 w-32" />
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-2 w-full" />
        <div className="grid grid-cols-2 gap-3">
          <Skeleton className="h-12" />
          <Skeleton className="h-12" />
        </div>
      </div>
    </div>
  );
}

/**
 * Audit tab — newest-first timeline of every create/update on this
 * contract. Each entry shows the actor (user or system) + the changed
 * fields with before → after values.
 */
function AuditTab({ contractId }: { contractId: string }) {
  const auditQuery = useContractAudit(contractId);

  if (auditQuery.isLoading) {
    return <AuditSkeleton />;
  }
  if (auditQuery.isError) {
    return (
      <div className="p-6 max-w-3xl">
        <div className="rounded-md border border-danger-500/30 bg-danger-500/5 p-4 text-[13px] text-danger-400">
          Couldn't load audit log.
        </div>
      </div>
    );
  }
  const entries = auditQuery.data ?? [];

  return (
    <div className="p-6 max-w-3xl" data-testid="ct-audit-pane">
      {entries.length === 0 ? (
        <div className="rounded-md border border-ink-600 border-dashed bg-ink-850 p-8 text-center text-[12.5px] text-ink-300">
          No audit entries yet. Edits and renewals will appear here.
        </div>
      ) : (
        <ol className="space-y-3" data-testid="ct-audit-list">
          {entries.map((e) => (
            <AuditEntryItem key={e.id} entry={e} />
          ))}
        </ol>
      )}
    </div>
  );
}

function AuditEntryItem({ entry }: { entry: ContractAuditEntry }) {
  const fields = useMemo(
    () => Object.entries(entry.changes),
    [entry.changes]
  );
  const actor = entry.userName ?? 'System';
  const when = formatTimestamp(entry.createdAt);
  return (
    <li
      className="rounded-md border border-ink-600 bg-ink-850 p-4"
      data-testid={`ct-audit-row-${entry.id}`}
    >
      <header className="flex items-baseline gap-2 mb-2">
        <Badge tone={entry.action === 'create' ? 'green' : 'blue'}>
          {entry.action === 'create' ? 'Created' : 'Updated'}
        </Badge>
        <span className="text-[13px] text-ink-100 font-medium">{actor}</span>
        {entry.userEmail && (
          <span className="text-[11.5px] mono text-ink-400">
            {entry.userEmail}
          </span>
        )}
        <span className="ml-auto text-[11.5px] mono text-ink-400">{when}</span>
      </header>
      {fields.length > 0 ? (
        <ul className="space-y-1 text-[12.5px]">
          {fields.map(([field, [before, after]]) => (
            <li
              key={field}
              className="grid grid-cols-[8rem,1fr] items-baseline gap-2"
            >
              <span className="text-ink-300 mono">{field}</span>
              <span className="min-w-0">
                {entry.action === 'create' ? (
                  <span className="text-ink-100 truncate">
                    {formatValue(after)}
                  </span>
                ) : (
                  <span>
                    <span className="text-ink-300 line-through truncate">
                      {formatValue(before)}
                    </span>
                    <span className="text-ink-400 mx-1.5">→</span>
                    <span className="text-ink-100 truncate">
                      {formatValue(after)}
                    </span>
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[12px] text-ink-400 italic">No fields changed.</p>
      )}
    </li>
  );
}

function AuditSkeleton() {
  return (
    <div className="p-6 max-w-3xl space-y-3" aria-busy="true">
      {Array.from({ length: 3 }).map((_, i) => (
        <div
          key={i}
          className="rounded-md border border-ink-600 bg-ink-850 p-4 space-y-2"
        >
          <div className="flex items-center gap-2">
            <Skeleton className="h-5 w-16" inline />
            <Skeleton className="h-3 w-32" inline />
            <Skeleton className="h-3 w-24 inline ml-auto" inline />
          </div>
          <Skeleton className="h-3 w-1/2" />
          <Skeleton className="h-3 w-2/3" />
        </div>
      ))}
    </div>
  );
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') return v;
  return String(v);
}

function formatYmd(ymd: string): string {
  // ymd is `YYYY-MM-DD` — render as `25 Apr 2026` (AEST display per CLAUDE.md).
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return ymd;
  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];
  return `${d} ${months[m - 1]} ${y}`;
}

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-AU', {
      timeZone: 'Australia/Sydney',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return iso;
  }
}

// ---- New tabs ----

function TicketsTab({ contractId }: { contractId: string }) {
  const ticketsQuery = useQuery({
    queryKey: ['contract-tickets', contractId],
    queryFn: () => beaconClient.get<{ tickets: Array<{
      number: string; subject: string; status: string;
      priority: string; assigneeUserId: string | null; createdAt: string;
    }> }>('/api/tickets'),
    staleTime: 30_000,
  });

  const tickets = (ticketsQuery.data?.tickets ?? []).filter(
    (t: Record<string, unknown>) => t.contractId === contractId
  );

  if (ticketsQuery.isLoading) return <div className="p-6"><Skeleton className="h-40" /></div>;

  return (
    <div className="p-6" data-testid="ct-tickets-pane">
      {tickets.length === 0 ? (
        <p className="text-[13px] text-ink-400">No tickets on this contract yet.</p>
      ) : (
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="text-left text-[10.5px] uppercase tracking-wider text-ink-300 border-b border-ink-600">
              <th className="py-2 pr-3 w-[70px]">Ticket</th>
              <th className="py-2 pr-3">Subject</th>
              <th className="py-2 pr-3 w-[110px]">Status</th>
              <th className="py-2 pr-3 w-[60px]">Pri</th>
              <th className="py-2 pr-3 w-[100px] text-right">Created</th>
            </tr>
          </thead>
          <tbody>
            {tickets.map((t) => (
              <tr key={t.number} className="border-b border-ink-600/60 hover:bg-ink-850">
                <td className="py-2.5 pr-3 mono text-ink-300">
                  <Link to={`/tickets/${t.number}`} className="hover:text-beacon-300">{t.number}</Link>
                </td>
                <td className="py-2.5 pr-3 text-ink-100 truncate max-w-0">
                  <span className="truncate inline-block max-w-full">{t.subject}</span>
                </td>
                <td className="py-2.5 pr-3"><Badge tone={statusTone(t.status)}>{t.status.replace('_', ' ')}</Badge></td>
                <td className="py-2.5 pr-3"><Badge tone={t.priority === 'p1' ? 'red' : t.priority === 'p2' ? 'amber' : 'slate'}>{t.priority.toUpperCase()}</Badge></td>
                <td className="py-2.5 pr-3 text-right mono text-ink-300 text-[11px]">{formatTimestamp(t.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ReportsTab({ contractId }: { contractId: string }) {
  const reportsQuery = useQuery({
    queryKey: ['contract-reports', contractId],
    queryFn: () => beaconClient.get<{ reports: Array<{
      id: string; status: string; periodStartDate: string;
      periodEndDate: string; generatedAt: string; sentAt: string | null;
    }> }>('/api/reports'),
    staleTime: 30_000,
  });

  const reports = (reportsQuery.data?.reports ?? []).filter(
    (r: Record<string, unknown>) => r.contractId === contractId
  );

  if (reportsQuery.isLoading) return <div className="p-6"><Skeleton className="h-40" /></div>;

  return (
    <div className="p-6" data-testid="ct-reports-pane">
      {reports.length === 0 ? (
        <p className="text-[13px] text-ink-400">No reports for this contract yet.</p>
      ) : (
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="text-left text-[10.5px] uppercase tracking-wider text-ink-300 border-b border-ink-600">
              <th className="py-2 pr-3">Period</th>
              <th className="py-2 pr-3 w-[90px]">Status</th>
              <th className="py-2 pr-3 w-[120px]">Generated</th>
              <th className="py-2 pr-3 w-[120px]">Sent</th>
            </tr>
          </thead>
          <tbody>
            {reports.map((r) => (
              <tr key={r.id} className="border-b border-ink-600/60 hover:bg-ink-850">
                <td className="py-2.5 pr-3 text-ink-100 mono">
                  <Link to={`/reports/${r.id}`} className="hover:text-beacon-300">
                    {r.periodStartDate} → {r.periodEndDate}
                  </Link>
                </td>
                <td className="py-2.5 pr-3">
                  <Badge tone={r.status === 'sent' ? 'green' : r.status === 'approved' ? 'blue' : 'neutral'}>
                    {r.status}
                  </Badge>
                </td>
                <td className="py-2.5 pr-3 mono text-ink-300 text-[11px]">{formatTimestamp(r.generatedAt)}</td>
                <td className="py-2.5 pr-3 mono text-ink-300 text-[11px]">{r.sentAt ? formatTimestamp(r.sentAt) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function UsersTab({ orgId }: { orgId: string }) {
  const usersQuery = useQuery({
    queryKey: ['org-users', orgId],
    queryFn: () => beaconClient.get<{ users: Array<{
      id: string; name: string; email: string; role: string;
    }> }>('/api/users'),
    staleTime: 60_000,
  });

  const orgUsers = (usersQuery.data?.users ?? []).filter(
    (u: Record<string, unknown>) => u.organizationId === orgId
  );

  if (usersQuery.isLoading) return <div className="p-6"><Skeleton className="h-40" /></div>;

  return (
    <div className="p-6" data-testid="ct-users-pane">
      {orgUsers.length === 0 ? (
        <p className="text-[13px] text-ink-400">No users in this organisation.</p>
      ) : (
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="text-left text-[10.5px] uppercase tracking-wider text-ink-300 border-b border-ink-600">
              <th className="py-2 pr-3">Name</th>
              <th className="py-2 pr-3">Email</th>
              <th className="py-2 pr-3 w-[120px]">Role</th>
            </tr>
          </thead>
          <tbody>
            {orgUsers.map((u) => (
              <tr key={u.id} className="border-b border-ink-600/60">
                <td className="py-2.5 pr-3 text-ink-100">{u.name}</td>
                <td className="py-2.5 pr-3 mono text-ink-300">{u.email}</td>
                <td className="py-2.5 pr-3"><Badge tone="neutral">{u.role.replace('_', ' ')}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

